import { useMemo, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import type { MatchSummary, TournamentListItem } from '@y-sports/api-client/generated-next-management/src/models';
import { useTranslation } from 'react-i18next';
import { PublicTournamentDataService, type PublicAdHocMatch } from '../../services/publicTournamentData';

type MatchBucket = 'live' | 'upcoming' | 'past';
type LiveMatch = MatchSummary & { tournamentName: string; bucket: MatchBucket; streamingUrl?: string | null };
type TeamColorFields = Pick<NonNullable<MatchSummary['teamA']>, 'homeColor' | 'awayColor' | 'goalkeeperColor'>;
type TeamWithColorAliases = NonNullable<MatchSummary['teamA']> & { home_color?: string; away_color?: string; goalkeeper_color?: string };

const isRunningMatch = (status?: string) => ['in_progress', 'running', 'paused'].includes((status || '').trim().toLowerCase());
const isPastMatch = (status?: string, matchTime?: Date | string | null) => {
  const normalized = (status || '').trim().toLowerCase();
  if (['finished', 'ended', 'completed', 'played', 'closed'].includes(normalized)) return true;
  if (['scheduled', 'not_started', 'pending'].includes(normalized)) return false;
  return Boolean(matchTime && dayjs(matchTime).isValid() && dayjs(matchTime).isBefore(dayjs()));
};
const getMatchBucket = (status?: string, matchTime?: Date | string | null): MatchBucket => isRunningMatch(status) ? 'live' : isPastMatch(status, matchTime) ? 'past' : 'upcoming';
const formatLiveTime = (value?: Date | string | null) => value && dayjs(value).isValid() ? dayjs(value).format('MMM D · HH:mm') : null;

const getNormalizedTeamColors = (team?: MatchSummary['teamA']): TeamColorFields => {
  const aliased = team as TeamWithColorAliases | undefined;
  return {
    homeColor: aliased?.homeColor || aliased?.home_color,
    awayColor: aliased?.awayColor || aliased?.away_color,
    goalkeeperColor: aliased?.goalkeeperColor || aliased?.goalkeeper_color,
  };
};
const getTeamDisplayColor = (team?: MatchSummary['teamA']) => {
  const colors = getNormalizedTeamColors(team);
  return colors.homeColor || colors.awayColor || colors.goalkeeperColor || undefined;
};

const mapAdHocMatch = (match: PublicAdHocMatch, translate: (key: string) => string): LiveMatch | null => {
  if (!match.id) return null;
  const mapTeam = (team: PublicAdHocMatch['teamA']) => ({
    id: team?.id,
    name: team?.name,
    homeColor: team?.details?.homeColor || team?.details?.home_color,
    awayColor: team?.details?.awayColor || team?.details?.away_color || team?.details?.visitorColor || team?.details?.visitor_color,
    goalkeeperColor: team?.details?.goalkeeperColor || team?.details?.goalkeeper_color || team?.details?.goalkeepColor || team?.details?.goalkeep_color,
  });
  return {
    id: match.id,
    matchTime: match.matchTime || undefined,
    streamingUrl: match.streamingUrl || undefined,
    status: match.status || undefined,
    teamA: mapTeam(match.teamA) as MatchSummary['teamA'],
    teamB: mapTeam(match.teamB) as MatchSummary['teamB'],
    teamAScore: match.teamAScore ?? 0,
    teamBScore: match.teamBScore ?? 0,
    court: match.court ? { id: match.court.id || undefined, name: match.court.name || undefined } : undefined,
    tournamentName: translate('liveScores.adHocGroup'),
    bucket: getMatchBucket(match.status || undefined, match.matchTime),
  } as LiveMatch;
};

export function FriendlyLiveScores() {
  const { t } = useTranslation();
  const [teamFilter, setTeamFilter] = useState('');
  const [activeTab, setActiveTab] = useState<MatchBucket>('live');

  const { data: allMatches = [], isLoading } = useQuery({
    queryKey: ['public-matches-live-upcoming-and-past'],
    queryFn: async () => {
      const tournaments = await PublicTournamentDataService.getTournamentList();
      const [perTournament, adHocMatches] = await Promise.all([
        Promise.all(tournaments.map(async (tournament: TournamentListItem) => {
          if (!tournament.id) return [] as LiveMatch[];
          try {
            const [schedule, teamsSummary] = await Promise.all([
              PublicTournamentDataService.getTournamentScheduleSummary(tournament.id),
              PublicTournamentDataService.getTournamentTeamsSummary(tournament.id),
            ]);
            const colorsById = new Map<string, TeamColorFields>();
            const colorsByName = new Map<string, TeamColorFields>();
            (teamsSummary.teams || []).forEach((team) => {
              const colors = getNormalizedTeamColors(team);
              if (team.id) colorsById.set(team.id, colors);
              if (team.name) colorsByName.set(team.name.toLowerCase(), colors);
            });
            const hydrateTeam = (team?: MatchSummary['teamA']) => {
              if (!team) return team;
              const colors = (team.id ? colorsById.get(team.id) : undefined) || (team.name ? colorsByName.get(team.name.toLowerCase()) : undefined);
              if (!colors) return team;
              const own = getNormalizedTeamColors(team);
              return { ...team, homeColor: own.homeColor || colors.homeColor, awayColor: own.awayColor || colors.awayColor, goalkeeperColor: own.goalkeeperColor || colors.goalkeeperColor };
            };
            return (schedule.matches || []).filter((match) => Boolean(match.id)).map((match) => ({
              ...match,
              teamA: hydrateTeam(match.teamA),
              teamB: hydrateTeam(match.teamB),
              streamingUrl: (match as MatchSummary & { streamingUrl?: string | null }).streamingUrl,
              tournamentName: tournament.name || t('eventDetail.fallbacks.tournament'),
              bucket: getMatchBucket(match.status, match.matchTime),
            }));
          } catch {
            return [] as LiveMatch[];
          }
        })),
        PublicTournamentDataService.getPublicAdHocMatches().catch(() => [] as PublicAdHocMatch[]),
      ]);
      return perTournament.flat()
        .concat(adHocMatches.map((match) => mapAdHocMatch(match, t)).filter((match): match is LiveMatch => Boolean(match)))
        .sort((left, right) => {
          const rank: Record<MatchBucket, number> = { live: 0, upcoming: 1, past: 2 };
          if (left.bucket !== right.bucket) return rank[left.bucket] - rank[right.bucket];
          const leftTime = dayjs(left.matchTime).valueOf() || Number.MAX_SAFE_INTEGER;
          const rightTime = dayjs(right.matchTime).valueOf() || Number.MAX_SAFE_INTEGER;
          return left.bucket === 'past' ? rightTime - leftTime : leftTime - rightTime;
        });
    },
    staleTime: 1000 * 20,
    refetchInterval: 1000 * 30,
  });

  const matchesByBucket = useMemo(() => ({
    live: allMatches.filter((match) => match.bucket === 'live'),
    upcoming: allMatches.filter((match) => match.bucket === 'upcoming'),
    past: allMatches.filter((match) => match.bucket === 'past'),
  }), [allMatches]);
  const activeMatches = matchesByBucket[activeTab];
  const filteredMatches = useMemo(() => {
    const query = teamFilter.trim().toLowerCase();
    return query ? activeMatches.filter((match) => `${match.teamA?.name || ''} ${match.teamB?.name || ''}`.toLowerCase().includes(query)) : activeMatches;
  }, [activeMatches, teamFilter]);

  const emptyMessage = {
    live: t('liveScores.emptyLive'),
    upcoming: t('liveScores.emptyUpcoming'),
    past: t('liveScores.emptyPast'),
  }[activeTab];

  return (
    <div className="page-stack">
      <section className="page-intro live-summary">
        <div className="page-intro__copy"><p className="eyebrow">{t('liveScores.status')}</p><h1 className="page-title">{t('liveScores.title')}</h1><p className="lead">{t('liveScores.subtitle')}</p></div>
        <div className="live-counts">
          <div className="live-count is-live"><span>{t('liveScores.status')}</span><strong>{matchesByBucket.live.length}</strong></div>
          <div className="live-count"><span>{t('liveScores.tabs.upcoming')}</span><strong>{matchesByBucket.upcoming.length}</strong></div>
          <div className="live-count"><span>{t('liveScores.tabs.past')}</span><strong>{matchesByBucket.past.length}</strong></div>
        </div>
      </section>

      <section className="filter-panel">
        <div className="filter-pills">
          {(['live', 'upcoming', 'past'] as MatchBucket[]).map((tab) => <button type="button" key={tab} className={`filter-pill ${activeTab === tab ? 'is-active' : ''}`} onClick={() => setActiveTab(tab)}>{t(`liveScores.tabs.${tab}`)} · {matchesByBucket[tab].length}</button>)}
        </div>
        {activeMatches.length > 0 && <input type="search" className="field-search" value={teamFilter} onChange={(event) => setTeamFilter(event.target.value)} placeholder={t('liveScores.teamFilterPlaceholder')} aria-label={t('liveScores.teamFilterLabel')} />}
      </section>

      {isLoading ? <div className="live-feed">{Array.from({ length: 4 }).map((_, index) => <div className="skeleton" key={index} />)}</div> : !activeMatches.length ? <div className="empty-state">{emptyMessage}</div> : !filteredMatches.length ? <div className="empty-state">{t('liveScores.emptyFiltered')}</div> : (
        <div className="live-feed">
          {filteredMatches.map((match) => {
            const teamAColor = getTeamDisplayColor(match.teamA);
            const teamBColor = getTeamDisplayColor(match.teamB);
            return <article className="live-match-row" key={match.id}>
              <div className="live-match__status">
                <span className={`status-badge ${activeTab === 'live' ? 'is-live' : ''}`}>{activeTab === 'live' ? t('liveScores.status') : t(`liveScores.tabs.${activeTab}`)}</span>
                <span className="mono text-muted">{formatLiveTime(match.matchTime) || t('eventDetail.fallbacks.tbd')}</span>
              </div>
              <div className="live-match__teams">
                <div className="live-match__team"><span><span className="team-dot" style={{ '--team-color': teamAColor || 'var(--ys-action)' } as CSSProperties} /> {match.teamA?.name || t('eventDetail.fallbacks.teamTbd')}</span><strong className="mono">{match.teamAScore ?? 0}</strong></div>
                <div className="live-match__team"><span><span className="team-dot" style={{ '--team-color': teamBColor || 'var(--ys-live)' } as CSSProperties} /> {match.teamB?.name || t('eventDetail.fallbacks.teamTbd')}</span><strong className="mono">{match.teamBScore ?? 0}</strong></div>
                <span className="live-match__meta">{match.tournamentName} · {match.court?.name || t('eventDetail.fallbacks.tba')}</span>
              </div>
              <div className="live-match__actions">
                {match.streamingUrl && <a className="button button--small button--live" href={match.streamingUrl} target="_blank" rel="noreferrer">{t('liveScores.watchStream')}</a>}
                {match.id && <Link className="button button--small button--quiet" to={`/scores/matches/${match.id}`}>{t('liveScores.openMatchCenter')} →</Link>}
              </div>
            </article>;
          })}
        </div>
      )}
    </div>
  );
}
